import React, { useEffect, useState } from "react";

import FormattedMessage from "@components/UI/FormattedMessage";
import Modal from "@components/UI/Modal";

type ButtonProps = {
  label: string;
  onClick?: () => void;
  Highlight?: boolean;
};

const getSafeId = (value?: string) =>
  value
    ?.replace(/<[^>]*>/g, "")
    .replace(/[^a-zA-Z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();

const canUseAsAriaId = (value: string) => !/\s/.test(value);

const BookingFormInfoModal = ({
  show,
  onClose,
  title,
  content,
  showErrorText,
  customButtons,
  contentId,
  modalClassName,
}: {
  show: boolean;
  onClose: () => void;
  title: string;
  content: string;
  showErrorText?: boolean;
  customButtons?: ButtonProps | ButtonProps[];
  contentId?: string;
  modalClassName?: string;
}): React.ReactElement => {
  const [showLoader, setShowLoader] = useState(!title && !content);
  const modalIdBase =
    getSafeId(contentId || title) || "booking-form-info-modal";
  const modalTitleId = `${modalIdBase}-title`;
  const modalContentId = contentId || `${modalIdBase}-content`;
  const modalDescriptionId = canUseAsAriaId(modalContentId)
    ? modalContentId
    : undefined;
  const hasDialogContent = !showLoader && !showErrorText;

  useEffect(() => {
    setShowLoader(!title && !content);
  }, [title, content]);

  const ButtonTemplate = ({ onClick, label, Highlight }: ButtonProps) => (
    <button
      type="button"
      onClick={onClick}
      className={`modal-alert-button ${
        Highlight ? "highlight" : "unhighlight"
      }`}
    >
      {label}
    </button>
  );

  const buttons = customButtons
    ? (Array.isArray(customButtons)
        ? customButtons
        : [customButtons]
      ).map((button, index) => <ButtonTemplate key={index} {...button} />)
    : null;

  return (
    <Modal
      innerClassName="booking-form-section-info-modal"
      isActive={show}
      onClose={onClose}
      initialFocusSelector=".modal-alert-button.highlight, .modal-alert-button"
      ariaLabelledBy={hasDialogContent ? modalTitleId : undefined}
      ariaDescribedBy={hasDialogContent ? modalDescriptionId : undefined}
    >
      {showErrorText && (
        <div
          className={`modal-section-wrapper${showErrorText ? "__error" : ""}`}
        >
          <span className="modal-section-error">
            <FormattedMessage id="Result.modalError" />
          </span>
        </div>
      )}

      {showLoader && !showErrorText && <div className="modal-section-loader" />}
      {!showLoader && !showErrorText && (
        <div className={`modal-section-wrapper ${modalClassName || ""}`}>
          <span id={modalTitleId} className="modal-section-title">
            {title}
          </span>
          <div
            id={modalContentId}
            className="modal-section-content"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: content }}
          />
          {customButtons && (
            <div className="modal-alert-buttons-wrapper">
              <div className="modal-alert-buttons">{buttons}</div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default React.memo(BookingFormInfoModal);
